import React from 'react'

interface CarouselProps {
  children: React.ReactNode[]
}

/**
 * Carousel component that displays a horizontal list of items with a sliding effect.
 * It allows users to select an item by clicking on it, and automatically cycles through the items.
 * @param children - The items to be displayed in the carousel.
 */
export const Carousel: React.FC<CarouselProps> = ({ children }) => {
  const [selectedIndex, setSelectedIndex] = React.useState(Math.floor(children.length / 2))
  const [lastClick, setLastClick] = React.useState(Date.now())

  const selectCard = (index: number) => {
    if (index === selectedIndex) return
    setSelectedIndex(index)
    setLastClick(Date.now())
  }

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (Date.now() - lastClick > 5000) {
        selectCard((selectedIndex + 1) % children.length)
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [lastClick, selectedIndex, children.length])

  return (
    <div
      className="relative max-w-[400px] overflow-hidden"
      style={{
        maskImage: 'linear-gradient(to right, transparent, white 10%, white 90%, transparent)',
      }}
    >
      <div
        className="flex transition-transform duration-500 ease-in-out"
        style={{
          transform: `translateX(${-selectedIndex * 200 + 100}px)`,
        }}
      >
        {React.Children.map(children, (child, index) => (
          <div
            className={`flex w-[200px] flex-shrink-0 transform items-center justify-center transition-all duration-500 ease-in-out ${
              selectedIndex === index ? 'z-10 scale-100 opacity-100' : 'pointer-events-none z-0 scale-90 opacity-25'
            }`}
          >
            {child}
          </div>
        ))}
      </div>
      {children.length > 1 && (
        <div className="mt-4 flex min-h-[30px] items-center justify-center">
          {children.map((_, index) => (
            <div
              key={index}
              className={`mx-2 h-[10px] w-[10px] cursor-pointer rounded-full ${
                selectedIndex === index ? 'bg-logo' : 'bg-gray-300 bg-opacity-30'
              }`}
              onClick={() => {
                selectCard(index)
                setLastClick(Date.now() + 30000)
              }}
            ></div>
          ))}
        </div>
      )}
    </div>
  )
}
